// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState } from 'react';

import type { PanoramaInfo } from '../../api/types';
import { describePlacementDa, headingCaveatDa, type PanoramaPlacement } from '../../viewport/panorama';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { IconButton } from '../IconButton';
import { PanoSegment } from './evidence/PanoSegment';
import styles from './PanoramaOverlay.module.css';

export interface PanoramaOverlayProps {
  panorama: PanoramaInfo;
  placement: PanoramaPlacement | null;
  /** Position in the walk, 1-based, for the `n af m` readout. */
  index: number;
  total: number;
  loading: boolean;
  error: Error | null;
  /** Whether the point cloud stays drawn inside the sphere. */
  showGeometry: boolean;
  onShowGeometryChange: (show: boolean) => void;
  onStep: (delta: number) => void;
  onExit: () => void;
}

/** A 16px stroked chevron, the Select's drawn sideways: `‹` / `›` for the walk. */
function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={direction === 'left' ? 'M10 4l-4 4 4 4' : 'M6 4l4 4-4 4'} />
    </svg>
  );
}

/**
 * The part of a load error worth showing after "Billedet kunne ikke
 * indlæses." — or nothing, when all there is is a stringified object.
 */
function readableDetail(error: Error): string | null {
  const message = error.message.trim();
  if (!message || message.startsWith('[object ')) return null;
  return message;
}

/**
 * The bar shown while standing inside a panorama on the Ground (`?pano=`,
 * PLAN.md decision 8) — `PanoramaBar` from the retired viewport page, in
 * Danish and over the tiles. `[` / `]` step and `Esc` leaves (the keys `rux
 * view` uses; bound by the Ground), and the controls name them. The caveat
 * line says when the heading is a guess: a wrong heading in a photographic
 * backdrop is invisible and misleading unless the user is told.
 *
 * "Del op" folds out SAM3 on the panorama being stood in (`PanoSegment`); it
 * stays open while `[` / `]` walk on, so a walk can segment each stop.
 */
export function PanoramaOverlay({
  panorama,
  placement,
  index,
  total,
  loading,
  error,
  showGeometry,
  onShowGeometryChange,
  onStep,
  onExit,
}: PanoramaOverlayProps) {
  const caveat = headingCaveatDa(placement);
  const [segmenting, setSegmenting] = useState(false);
  const detail = error ? readableDetail(error) : null;

  return (
    <div className={styles.bar} role="group" aria-label="360° panorama">
      <div className={styles.controls}>
        <IconButton
          variant="filled"
          size="md"
          label="Forrige panorama"
          title="Forrige panorama ( [ )"
          onClick={() => onStep(-1)}
          disabled={total < 2}
        >
          <ChevronIcon direction="left" />
        </IconButton>
        <IconButton
          variant="filled"
          size="md"
          label="Næste panorama"
          title="Næste panorama ( ] )"
          onClick={() => onStep(1)}
          disabled={total < 2}
        >
          <ChevronIcon direction="right" />
        </IconButton>
      </div>

      <div className={styles.text}>
        <span className={styles.name} title={panorama.filename}>
          {panorama.filename}
        </span>
        <span className={styles.detail} aria-live="polite">
          {index} af {total} · {describePlacementDa(panorama, placement)}
          {loading ? ' · indlæser…' : ''}
        </span>
      </div>

      <div className={styles.actions}>
        <Checkbox
          className={styles.toggle}
          label="Vis modellen ovenpå"
          muted
          checked={showGeometry}
          onChange={(event) => onShowGeometryChange(event.target.checked)}
        />

        <Button
          variant="filled"
          size="md"
          aria-expanded={segmenting}
          onClick={() => setSegmenting((v) => !v)}
          title="SAM3 på hele optagelsen: tekstprompts, konfidens, fliser"
        >
          {segmenting ? 'Skjul Del op' : 'Del op'}
        </Button>

        <Button variant="filled" size="md" onClick={onExit} title="Luk panoramaet (Esc)">
          Luk
        </Button>
      </div>

      {caveat && <p className={styles.caveat}>{caveat}</p>}
      {error && (
        <p className={styles.error} role="alert">
          Billedet kunne ikke indlæses.{detail ? ` ${detail}` : ''}
        </p>
      )}

      {segmenting && (
        <div className={styles.segment}>
          <PanoSegment panoramaId={panorama.id} />
        </div>
      )}
    </div>
  );
}
